import { useEffect, useState, type FormEvent } from "react";
import { createPortal } from "react-dom";

import { useAuth } from "../auth/AuthContext";
import { api, ApiError } from "../lib/api";

// «Mi cuenta» (2026-10-09): el staff (superadmin/organizador) no tenía dónde
// guardar su celular y la prueba de avisos decía «Sin número en tu perfil».
// Solo edita users.phone de la sesión; correo y roles no se tocan aquí.
export default function MyAccountDialog({ onClose }: { onClose: () => void }) {
  const { token, user, refresh } = useAuth();
  const [phone, setPhone] = useState(user?.phone ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) return;
    const digits = phone.replace(/\D/g, "");
    if (digits && digits.length < 10) {
      setError("Escribe un celular de 10 dígitos.");
      return;
    }
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const response = await api.auth.updateOwnPhone(token, phone.trim() || null);
      setPhone(response.data.user.phone ?? "");
      setSaved(true);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError && err.status === 422 ? "Escribe un celular de 10 dígitos." : "No pudimos guardar tu teléfono. Intenta otra vez.");
    } finally {
      setSaving(false);
    }
  }

  return createPortal(
    <div className="rr-change-sheet" role="dialog" aria-modal="true" aria-labelledby="my-account-title">
      <div className="rr-change-sheet__backdrop" onClick={onClose} />
      <div className="rr-change-sheet__panel">
        <header className="rr-change-sheet__header">
          <div>
            <span className="player-pro-eyebrow">Mi cuenta</span>
            <strong id="my-account-title">{user?.full_name ?? ""}</strong>
          </div>
          <button type="button" className="rr-change-sheet__close" onClick={onClose} aria-label="Cerrar">
            ×
          </button>
        </header>
        <form onSubmit={(event) => void handleSubmit(event)} noValidate>
          <div className="rr-change-sheet__body">
            <p className="muted">{user?.email}</p>
            <label className="rr-change-sheet__field">
              Mi teléfono (celular)
              <input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="10 dígitos"
                value={phone}
                onChange={(event) => {
                  setPhone(event.target.value);
                  setSaved(false);
                }}
                disabled={saving}
              />
            </label>
            <p className="muted">Lo usamos para enviarte las pruebas de avisos por WhatsApp. Déjalo vacío para quitarlo.</p>
            {error && (
              <p role="alert" className="form-error">
                {error}
              </p>
            )}
            {saved && <p className="form-message">Teléfono guardado.</p>}
            <button className="btn" type="submit" disabled={saving}>
              {saving ? "Guardando…" : "Guardar teléfono"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
